/**
 * gameEventDispatch.ts
 * Lightweight callback registry for game-loop events.
 *
 * Engine code (physicsStep, collectibleStep, CheckpointSystem) fires events
 * by calling the `dispatch.*()` functions below. The React layer registers
 * handlers via `registerGameEventHandlers()` — typically to trigger haptics,
 * narrative barks, and audio cues.
 *
 * No React imports. No async. All callbacks must return synchronously.
 *
 * @module gameEventDispatch
 */

import Logger from '../utils/logger';

/** Callback signatures for each game event. */
export interface GameEventHandlers {
  /** Nova just landed after being airborne. */
  onLand: (() => void) | null;
  /** Nova took damage from an enemy. */
  onHurt: (() => void) | null;
  /** A Star Seed was collected. */
  onSeedCollected: (() => void) | null;
  /** A Constellation Shard was collected. */
  onShardCollected: (() => void) | null;
  /** A Heart Fragment was collected. */
  onHeartFragmentCollected: (() => void) | null;
  /** A Weapon Pickup was collected — Nova’s active weapon and ammo changed. */
  onWeaponPickupCollected: (() => void) | null;
  /** A checkpoint was activated. */
  onCheckpointActivated: (() => void) | null;
  /** Nova's health dropped to 1 (low health warning). */
  onLowHealth: (() => void) | null;
  /** Nova died (health reached 0, fell into a pit, or hit a death-plane). */
  onDeath: (() => void) | null;
}

const _handlers: GameEventHandlers = {
  onLand: null,
  onHurt: null,
  onSeedCollected: null,
  onShardCollected: null,
  onHeartFragmentCollected: null,
  onWeaponPickupCollected: null,
  onCheckpointActivated: null,
  onLowHealth: null,
  onDeath: null,
};

/**
 * Register game event handlers. Pass null for any slot to clear it.
 * @param handlers - Partial set of handlers to register.
 */
export function registerGameEventHandlers(handlers: Partial<GameEventHandlers>): void {
  Object.assign(_handlers, handlers);
  Logger.debug('GameEventDispatch', 'Handlers registered');
}

/**
 * Clear all registered handlers. Call on scene destroy.
 */
export function clearGameEventHandlers(): void {
  _handlers.onLand = null;
  _handlers.onHurt = null;
  _handlers.onSeedCollected = null;
  _handlers.onShardCollected = null;
  _handlers.onHeartFragmentCollected = null;
  _handlers.onWeaponPickupCollected = null;
  _handlers.onCheckpointActivated = null;
  _handlers.onLowHealth = null;
  _handlers.onDeath = null;
}

// DECISION: Wrap handler invocations in try/catch. A throwing UI-layer
// callback must never bubble into the engine tick and break the game loop.
/**
 * Safely invoke a handler, catching and logging any errors.
 * @param name - Event name for the log message.
 * @param handler - The handler to invoke, or null.
 */
function _safeInvoke(name: string, handler: (() => void) | null): void {
  if (!handler) return;
  try {
    handler();
  } catch (err) {
    Logger.warn('GameEventDispatch', `Handler for ${name} threw`, err);
  }
}

/** Dispatch namespace — called from engine code. */
export const dispatch = {
  land(): void { _safeInvoke('land', _handlers.onLand); },
  hurt(): void { _safeInvoke('hurt', _handlers.onHurt); },
  seedCollected(): void { _safeInvoke('seedCollected', _handlers.onSeedCollected); },
  shardCollected(): void { _safeInvoke('shardCollected', _handlers.onShardCollected); },
  heartFragmentCollected(): void { _safeInvoke('heartFragmentCollected', _handlers.onHeartFragmentCollected); },
  weaponPickupCollected(): void { _safeInvoke('weaponPickupCollected', _handlers.onWeaponPickupCollected); },
  checkpointActivated(): void { _safeInvoke('checkpointActivated', _handlers.onCheckpointActivated); },
  lowHealth(): void { _safeInvoke('lowHealth', _handlers.onLowHealth); },
  death(): void { _safeInvoke('death', _handlers.onDeath); },
};
